/**
 * Avatar upload component with image preview
 */

import { useState, useRef, ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@/services/user.service';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Upload, User } from 'lucide-react';
import { queryKeys } from '@/lib/query-client';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  displayName: string;
  onUploadComplete?: () => void;
}

export function AvatarUpload({
  currentAvatarUrl,
  displayName,
  onUploadComplete,
}: AvatarUploadProps) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  // Mutation for getting upload URL
  const getUploadUrlMutation = useMutation({
    mutationFn: async ({ contentType, fileSize }: { contentType: string; fileSize: number }) => {
      if (!idToken) throw new Error('Not authenticated');
      return await userService.getAvatarUploadUrl(idToken, { contentType, fileSize });
    },
  });

  // Mutation for uploading to S3
  const uploadToS3Mutation = useMutation({
    mutationFn: async ({ uploadUrl, file }: { uploadUrl: string; file: File }) => {
      return await userService.uploadAvatar(uploadUrl, file);
    },
  });

  // Mutation for confirming upload
  const confirmUploadMutation = useMutation({
    mutationFn: async (avatarKey: string) => {
      if (!idToken) throw new Error('Not authenticated');
      return await userService.confirmAvatarUpload(idToken, { avatarKey });
    },
    onSuccess: () => {
      // Invalidate user query to refetch with new avatar
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });

      // Clear preview and file
      setPreviewUrl(null);
      setSelectedFile(null);

      if (onUploadComplete) {
        onUploadComplete();
      }
    },
  });

  const isUploading =
    getUploadUrlMutation.isPending ||
    uploadToS3Mutation.isPending ||
    confirmUploadMutation.isPending;

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError('');

    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('Image must be smaller than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setError('');

    try {
      // Step 1: Get presigned upload URL
      const { uploadUrl, avatarKey } = await getUploadUrlMutation.mutateAsync({
        contentType: selectedFile.type,
        fileSize: selectedFile.size,
      });

      // Step 2: Upload to S3
      await uploadToS3Mutation.mutateAsync({ uploadUrl, file: selectedFile });

      // Step 3: Confirm upload with backend
      await confirmUploadMutation.mutateAsync(avatarKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayAvatarUrl = previewUrl || currentAvatarUrl;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Avatar className="h-24 w-24">
          {displayAvatarUrl ? (
            <AvatarImage src={displayAvatarUrl} alt={displayName} />
          ) : (
            <AvatarFallback className="text-2xl">{getInitials(displayName)}</AvatarFallback>
          )}
        </Avatar>

        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />

          {selectedFile ? (
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={isUploading} size="sm">
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
              <Button onClick={handleCancel} disabled={isUploading} variant="outline" size="sm">
                Cancel
              </Button>
            </div>
          ) : (
            <Button onClick={handleButtonClick} disabled={isUploading} variant="outline" size="sm">
              <User className="mr-2 h-4 w-4" />
              Change Avatar
            </Button>
          )}

          <p className="mt-2 text-xs text-muted-foreground">JPEG, PNG, GIF, or WebP. Max 5MB.</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {selectedFile && (
        <div className="text-sm text-muted-foreground">
          Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
        </div>
      )}
    </div>
  );
}
